// "Filter on anything" for the Tenants and Signups lists (API_CONTRACT.md ›
// Filters; backend services/list-filters.ts). The server lists each field, its
// operators and its choices (the facets endpoints); a filter goes to the list as
// `f=field:op:value`, the value URL-encoded items joined by commas, so a value
// may itself hold a comma or a colon. The same strings live in the page's URL.

export const FIELD_TYPES = ['choice', 'set', 'text', 'date', 'number', 'bool'] as const
export type FieldType = (typeof FIELD_TYPES)[number]

export type FilterOp =
  | 'is' | 'is_not' | 'any_of' | 'contains' | 'before' | 'after' | 'between' | 'last_days' | 'min' | 'max' | 'is_empty' | 'is_not_empty'

export interface FacetChoice {
  value: string
  label: string
  /** How many rows hold it, for a choice read from the data. */
  count?: number
}

export interface FacetField {
  key: string
  label: string
  group: string
  type: FieldType
  ops: FilterOp[]
  sortable: boolean
  choices?: FacetChoice[]
}

export interface FacetsResponse {
  fields: FacetField[]
}

export interface ListFilter {
  field: string
  op: FilterOp
  values: string[]
}

export const OP_LABELS: Record<FilterOp, string> = {
  is: 'is',
  is_not: 'is not',
  any_of: 'is any of',
  contains: 'contains',
  before: 'is before',
  after: 'is after',
  between: 'is between',
  last_days: 'is in the last',
  min: 'is at least',
  max: 'is at most',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
}

/** How many values an operator takes: none, one, two, or a list. */
export function arity(op: FilterOp): 0 | 1 | 2 | 'many' {
  if (op === 'is_empty' || op === 'is_not_empty') return 0
  if (op === 'between') return 2
  if (op === 'any_of') return 'many'
  return 1
}

/** `field:op:value` for the API and the URL. */
export function encodeFilter(f: ListFilter): string {
  const head = `${f.field}:${f.op}`
  return f.values.length ? `${head}:${f.values.map(encodeURIComponent).join(',')}` : head
}

/** The reverse, or null for something that isn't one (an old or hand-edited link). */
export function decodeFilter(raw: string): ListFilter | null {
  const first = raw.indexOf(':')
  if (first <= 0) return null
  const second = raw.indexOf(':', first + 1)
  const field = raw.slice(0, first)
  const op = (second < 0 ? raw.slice(first + 1) : raw.slice(first + 1, second)) as FilterOp
  if (!(op in OP_LABELS)) return null
  const rest = second < 0 ? '' : raw.slice(second + 1)
  try {
    return { field, op, values: rest === '' ? [] : rest.split(',').map(decodeURIComponent) }
  } catch {
    return null
  }
}

/** Every `f` in the page's URL. */
export function filtersFromParams(p: URLSearchParams): ListFilter[] {
  return p.getAll('f').map(decodeFilter).filter((f): f is ListFilter => f !== null)
}

/** The chip's words: "Plan is any of Pro, Starter". */
export function describeFilter(f: ListFilter, field: FacetField | undefined): string {
  const label = field?.label ?? f.field
  const name = (v: string) => field?.choices?.find((c) => c.value === v)?.label ?? (field?.type === 'bool' ? (v === 'true' ? 'yes' : 'no') : v)
  const op = OP_LABELS[f.op]
  if (field?.type === 'bool') return `${label}: ${name(f.values[0] ?? '')}`
  switch (arity(f.op)) {
    case 0:
      return `${label} ${op}`
    case 2:
      return `${label} ${op} ${f.values[0]} and ${f.values[1]}`
    default:
      if (f.op === 'last_days') return `${label} ${op} ${f.values[0]} days`
      return `${label} ${op} ${f.values.map(name).join(', ')}`
  }
}
